import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { departmentsAPI } from "../../../services/api/departments.api";
import { locationAPI } from "../../../services/api/location.api";
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  ContactPhone as ContactIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from "@mui/icons-material";
import { ROUTES } from "../../../constants";
import { employeesAPI } from "../../../services/api/employees.api";
import rolesAPI from "../../../services/api/roles.api";
import useNotification from "../../../hooks/common/useNotification";

// Custom LoadingButton component
const LoadingButton = ({
  loading,
  children,
  startIcon,
  disabled,
  onClick,
  variant = "contained",
  size = "medium",
  fullWidth = false,
  sx = {},
  ...props
}) => {
  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      startIcon={loading ? <CircularProgress size={16} /> : startIcon}
      sx={{ position: "relative", ...sx }}
      {...props}
    >
      {children}
    </Button>
  );
};

// Format salary with comma separators
const formatSalary = (value) => {
  if (!value) return "";
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Parse salary removing commas
const parseSalary = (value) => {
  if (!value) return "";
  return value.replace(/,/g, "");
};

// Format NIDA with proper separators
const formatNIDA = (value) => {
  if (!value) return "";
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // Tanzania NIDA format: 20000424-15112-0000-123
  if (digits.length <= 8) {
    return digits;
  } else if (digits.length <= 13) {
    return `${digits.slice(0, 8)}-${digits.slice(8)}`;
  } else if (digits.length <= 17) {
    return `${digits.slice(0, 8)}-${digits.slice(8, 13)}-${digits.slice(13)}`;
  } else {
    return `${digits.slice(0, 8)}-${digits.slice(8, 13)}-${digits.slice(13, 17)}-${digits.slice(17, 20)}`;
  }
};

// Validate NIDA format
const validateNIDA = (value) => {
  if (!value) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length === 20;
};

// Required field label component
const RequiredLabel = ({ children }) => (
  <span>
    {children} <span style={{ color: "red" }}>*</span>
  </span>
);

const steps = [
  {
    label: "Personal Information",
    icon: <PersonIcon />,
  },
  {
    label: "Contact & Personal Data",
    icon: <ContactIcon />,
  },
  {
    label: "Employment & Roles",
    icon: <WorkIcon />,
  },
];

const contactPersonalDataSchema = Yup.object({
  personalEmployeeData: Yup.object({
    region_id: Yup.number().required("Region is required"),
    district_id: Yup.number().required("District is required"),
    education_level: Yup.string().required("Education level is required"),
  }),
  address: Yup.string().required("Address is required"),
  emergency_contacts: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("Emergency contact name is required"),
        phone_number: Yup.string().required(
          "Emergency contact phone is required"
        ),
        relationship: Yup.string().required("Relationship is required"),
      })
    )
    .min(1, "At least one emergency contact is required"),
});

const employmentRolesSchema = Yup.object({
  basicEmployeeData: Yup.object({
    department_id: Yup.number().required("Department is required"),
    designation: Yup.string().required("Designation is required"),
    salary: Yup.number()
      .positive("Salary must be positive")
      .required("Salary is required"),
    date_joined: Yup.date().required("Date joined is required"),
    employment_type: Yup.string().required("Employment type is required"),
    bank_name: Yup.string().required("Bank name is required"),
    account_number: Yup.string().required("Account number is required"),
  }),
  role_ids: Yup.array()
    .of(Yup.number())
    .min(1, "At least one role must be assigned")
    .required("Role assignment is required"),
});

// Emergency Contact Component
const EmergencyContactSection = ({ formik }) => {
  const addEmergencyContact = () => {
    const currentContacts = formik.values.emergency_contacts || [];
    formik.setFieldValue("emergency_contacts", [
      ...currentContacts,
      { name: "", phone_number: "", relationship: "" },
    ]);
  };

  const removeEmergencyContact = (index) => {
    const currentContacts = formik.values.emergency_contacts || [];
    const updatedContacts = currentContacts.filter((_, i) => i !== index);
    formik.setFieldValue("emergency_contacts", updatedContacts);
  };

  const updateEmergencyContact = (index, field, value) => {
    const currentContacts = formik.values.emergency_contacts || [];
    const updatedContacts = [...currentContacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    formik.setFieldValue("emergency_contacts", updatedContacts);
  };

  const emergencyContacts = formik.values.emergency_contacts || [
    { name: "", phone_number: "", relationship: "" },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          <RequiredLabel>Emergency Contacts</RequiredLabel>
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={addEmergencyContact}
          variant="outlined"
          size="small"
        >
          Add Contact
        </Button>
      </Box>

      {emergencyContacts.map((contact, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2">
              Emergency Contact {index + 1}
            </Typography>
            {emergencyContacts.length > 1 && (
              <IconButton
                onClick={() => removeEmergencyContact(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Full Name"
                value={contact.name || ""}
                onChange={(e) =>
                  updateEmergencyContact(index, "name", e.target.value)
                }
                error={
                  formik.touched.emergency_contacts?.[index]?.name &&
                  Boolean(formik.errors.emergency_contacts?.[index]?.name)
                }
                helperText={
                  formik.touched.emergency_contacts?.[index]?.name &&
                  formik.errors.emergency_contacts?.[index]?.name
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone Number"
                value={contact.phone_number || ""}
                onChange={(e) =>
                  updateEmergencyContact(index, "phone_number", e.target.value)
                }
                error={
                  formik.touched.emergency_contacts?.[index]?.phone_number &&
                  Boolean(
                    formik.errors.emergency_contacts?.[index]?.phone_number
                  )
                }
                helperText={
                  formik.touched.emergency_contacts?.[index]?.phone_number &&
                  formik.errors.emergency_contacts?.[index]?.phone_number
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={contact.relationship || ""}
                  onChange={(e) =>
                    updateEmergencyContact(
                      index,
                      "relationship",
                      e.target.value
                    )
                  }
                  label="Relationship"
                  error={
                    formik.touched.emergency_contacts?.[index]?.relationship &&
                    Boolean(
                      formik.errors.emergency_contacts?.[index]?.relationship
                    )
                  }
                >
                  <MenuItem value="spouse">Spouse</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="sibling">Sibling</MenuItem>
                  <MenuItem value="child">Child</MenuItem>
                  <MenuItem value="friend">Friend</MenuItem>
                  <MenuItem value="colleague">Colleague</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {formik.touched.emergency_contacts?.[index]?.relationship &&
                  formik.errors.emergency_contacts?.[index]?.relationship && (
                    <FormHelperText error>
                      {formik.errors.emergency_contacts?.[index]?.relationship}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      ))}
    </Box>
  );
};

// Next of Kin Component
const NextOfKinSection = ({ formik }) => {
  const addNextOfKin = () => {
    const currentKin = formik.values.next_of_kin || [];
    formik.setFieldValue("next_of_kin", [
      ...currentKin,
      { name: "", phone_number: "", relationship: "", percentage: 0 },
    ]);
  };

  const removeNextOfKin = (index) => {
    const currentKin = formik.values.next_of_kin || [];
    const updatedKin = currentKin.filter((_, i) => i !== index);
    formik.setFieldValue("next_of_kin", updatedKin);
  };

  const updateNextOfKin = (index, field, value) => {
    const currentKin = formik.values.next_of_kin || [];
    const updatedKin = [...currentKin];
    updatedKin[index] = { ...updatedKin[index], [field]: value };
    formik.setFieldValue("next_of_kin", updatedKin);
  };

  const nextOfKin = formik.values.next_of_kin || [
    { name: "", phone_number: "", relationship: "", percentage: 100 },
  ];
  const totalPercentage = nextOfKin.reduce(
    (sum, kin) => sum + (parseFloat(kin.percentage) || 0),
    0
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Next of Kin</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={addNextOfKin}
          variant="outlined"
          size="small"
        >
          Add Next of Kin
        </Button>
      </Box>

      {totalPercentage !== 100 && nextOfKin.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Total percentage must equal 100%. Current total: {totalPercentage}%
        </Alert>
      )}

      {nextOfKin.map((kin, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2">Next of Kin {index + 1}</Typography>
            {nextOfKin.length > 1 && (
              <IconButton
                onClick={() => removeNextOfKin(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Full Name"
                value={kin.name || ""}
                onChange={(e) => updateNextOfKin(index, "name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone Number"
                value={kin.phone_number || ""}
                onChange={(e) =>
                  updateNextOfKin(index, "phone_number", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={kin.relationship || ""}
                  onChange={(e) =>
                    updateNextOfKin(index, "relationship", e.target.value)
                  }
                  label="Relationship"
                >
                  <MenuItem value="spouse">Spouse</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="sibling">Sibling</MenuItem>
                  <MenuItem value="child">Child</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Percentage (%)"
                type="number"
                value={kin.percentage || ""}
                onChange={(e) =>
                  updateNextOfKin(
                    index,
                    "percentage",
                    parseFloat(e.target.value) || 0
                  )
                }
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}
    </Box>
  );
};

// Personal Information Form (Basic Info + Bio Data combined)
const PersonalInfoForm = ({ formik, editMode }) => (
  <Box>
    {/* Default Password Info */}
    {/* {!editMode && (
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Default Password:</strong> The employee will be created with a
          default password using the format: <code>Firstname@2025</code> (e.g.,
          John@2025)
        </Typography>
      </Alert>
    )} */}

    {/* Basic Information Section */}
    <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
      Basic Information
    </Typography>
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="First Name"
          name="first_name"
          value={formik.values.first_name}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.first_name)}
          helperText={formik.errors.first_name}
          required
          InputLabelProps={{
            sx: {
              color: formik.errors.first_name ? 'error.main' : undefined,
              '& .MuiFormLabel-asterisk': {
                color: formik.errors.first_name ? 'error.main' : 'error.main',
              },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Middle Name"
          name="middle_name"
          value={formik.values.middle_name}
          onChange={formik.handleChange}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Surname"
          name="sur_name"
          value={formik.values.sur_name}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.sur_name)}
          helperText={formik.errors.sur_name}
          required
          InputLabelProps={{
            sx: {
              color: formik.errors.sur_name ? 'error.main' : undefined,
              '& .MuiFormLabel-asterisk': {
                color: formik.errors.sur_name ? 'error.main' : 'error.main',
              },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.email)}
          helperText={formik.errors.email}
          required
          InputLabelProps={{
            sx: {
              color: formik.errors.email ? 'error.main' : undefined,
              '& .MuiFormLabel-asterisk': {
                color: formik.errors.email ? 'error.main' : 'error.main',
              },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Phone Number"
          name="phone_number"
          value={formik.values.phone_number}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.phone_number)}
          helperText={formik.errors.phone_number}
          placeholder="06XXXXXXXX or 07XXXXXXXX"
          required
          InputLabelProps={{
            sx: {
              color: formik.errors.phone_number ? 'error.main' : undefined,
              '& .MuiFormLabel-asterisk': {
                color: formik.errors.phone_number ? 'error.main' : 'error.main',
              },
            },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth required error={Boolean(formik.errors.gender)}>
          <InputLabel
            sx={{
              color: formik.errors.gender ? 'error.main' : undefined,
              '& .MuiFormLabel-asterisk': {
                color: formik.errors.gender ? 'error.main' : 'error.main',
              },
            }}
          >
            Gender
          </InputLabel>
          <Select
            name="gender"
            value={formik.values.gender}
            onChange={formik.handleChange}
            label="Gender"
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </Select>
          {formik.errors.gender && (
            <FormHelperText error>{formik.errors.gender}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={editMode ? 12 : 6}>
        <FormControl fullWidth required>
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={formik.values.status}
            onChange={formik.handleChange}
            label="Status"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    <Divider sx={{ my: 3 }} />

    {/* Bio Data Section */}
    <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
      Biographical Data
    </Typography>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Date of Birth"
          name="bioData.dob"
          type="date"
          value={formik.values.bioData?.dob || ""}
          onChange={formik.handleChange}
          error={
            formik.touched.bioData?.dob && Boolean(formik.errors.bioData?.dob)
          }
          helperText={formik.touched.bioData?.dob && formik.errors.bioData?.dob}
          InputLabelProps={{ shrink: true }}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Marital Status</InputLabel>
          <Select
            name="bioData.marital_status"
            value={formik.values.bioData?.marital_status || ""}
            onChange={formik.handleChange}
            label="Marital Status"
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="widowed">Widowed</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Blood Group</InputLabel>
          <Select
            name="bioData.blood_group"
            value={formik.values.bioData?.blood_group || ""}
            onChange={formik.handleChange}
            label="Blood Group"
          >
            <MenuItem value="">Select Blood Group</MenuItem>
            <MenuItem value="A+">A+</MenuItem>
            <MenuItem value="A-">A-</MenuItem>
            <MenuItem value="B+">B+</MenuItem>
            <MenuItem value="B-">B-</MenuItem>
            <MenuItem value="AB+">AB+</MenuItem>
            <MenuItem value="AB-">AB-</MenuItem>
            <MenuItem value="O+">O+</MenuItem>
            <MenuItem value="O-">O-</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="National ID (NIDA)"
          name="bioData.national_id"
          value={formik.values.bioData?.national_id || ""}
          onChange={(e) => {
            const formatted = formatNIDA(e.target.value);
            formik.setFieldValue("bioData.national_id", formatted);
          }}
          error={
            formik.touched.bioData?.national_id &&
            Boolean(formik.errors.bioData?.national_id)
          }
          helperText={
            formik.touched.bioData?.national_id &&
            formik.errors.bioData?.national_id
          }
          placeholder="********-*****-****-***"
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Fingerprint ID"
          name="bioData.fingerprint_id"
          value={formik.values.bioData?.fingerprint_id || ""}
          onChange={formik.handleChange}
        />
      </Grid>
    </Grid>
  </Box>
);

// Contact & Personal Data Form
const ContactPersonalDataForm = ({
  formik,
  regions,
  districts,
  loadingRegions,
  loadingDistricts,
  onRegionChange,
}) => (
  <Box>
    {/* Location and Education Section */}
    <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
      Location & Education
    </Typography>
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          required
          error={
            formik.touched.personalEmployeeData?.region_id &&
            Boolean(formik.errors.personalEmployeeData?.region_id)
          }
        >
          <InputLabel>Region</InputLabel>
          <Select
            name="personalEmployeeData.region_id"
            value={formik.values.personalEmployeeData?.region_id || ""}
            onChange={(e) => {
              formik.handleChange(e);
              onRegionChange(e.target.value);
            }}
            label="Region"
            disabled={loadingRegions}
          >
            <MenuItem value="">Select Region</MenuItem>
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                {region.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.personalEmployeeData?.region_id &&
            formik.errors.personalEmployeeData?.region_id && (
              <FormHelperText>
                {formik.errors.personalEmployeeData?.region_id}
              </FormHelperText>
            )}
          {loadingRegions && (
            <FormHelperText>Loading regions...</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          required
          error={
            formik.touched.personalEmployeeData?.district_id &&
            Boolean(formik.errors.personalEmployeeData?.district_id)
          }
        >
          <InputLabel>District</InputLabel>
          <Select
            name="personalEmployeeData.district_id"
            value={formik.values.personalEmployeeData?.district_id || ""}
            onChange={formik.handleChange}
            label="District"
            disabled={
              loadingDistricts || !formik.values.personalEmployeeData?.region_id
            }
          >
            <MenuItem value="">Select District</MenuItem>
            {districts.map((district) => (
              <MenuItem key={district.id} value={district.id}>
                {district.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.personalEmployeeData?.district_id &&
            formik.errors.personalEmployeeData?.district_id && (
              <FormHelperText>
                {formik.errors.personalEmployeeData?.district_id}
              </FormHelperText>
            )}
          {loadingDistricts && (
            <FormHelperText>Loading districts...</FormHelperText>
          )}
          {!formik.values.personalEmployeeData?.region_id && (
            <FormHelperText>Please select a region first</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Education Level</InputLabel>
          <Select
            name="personalEmployeeData.education_level"
            value={formik.values.personalEmployeeData?.education_level || ""}
            onChange={formik.handleChange}
            label="Education Level"
          >
            <MenuItem value="">Select Education Level</MenuItem>
            <MenuItem value="Primary">Primary</MenuItem>
            <MenuItem value="Secondary">Secondary</MenuItem>
            <MenuItem value="Certificate">Certificate</MenuItem>
            <MenuItem value="Diploma">Diploma</MenuItem>
            <MenuItem value="Bachelor">Bachelor</MenuItem>
            <MenuItem value="Master">Master</MenuItem>
            <MenuItem value="PhD">PhD</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address"
          name="address"
          multiline
          rows={2}
          value={formik.values.address || ""}
          onChange={formik.handleChange}
          error={formik.touched.address && Boolean(formik.errors.address)}
          helperText={formik.touched.address && formik.errors.address}
          placeholder="PO BOX 881 Dar es Salaam, Tanzania"
          required
        />
      </Grid>
    </Grid>

    <Divider sx={{ my: 3 }} />
    <EmergencyContactSection formik={formik} />

    <Divider sx={{ my: 3 }} />
    <NextOfKinSection formik={formik} />
  </Box>
);

// Employment & Roles Form (combined without tabs)
const EmploymentRolesForm = ({
  formik,
  departments,
  departmentsLoading,
  availableRoles,
  rolesLoading,
}) => {
  return (
    <Box>
      {/* Employment Data Section */}
      <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
        Employment Information
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Registration Number"
            name="basicEmployeeData.registration_number"
            value={formik.values.basicEmployeeData?.registration_number || ""}
            onChange={formik.handleChange}
            error={
              formik.touched.basicEmployeeData?.registration_number &&
              Boolean(formik.errors.basicEmployeeData?.registration_number)
            }
            helperText={
              formik.touched.basicEmployeeData?.registration_number &&
              formik.errors.basicEmployeeData?.registration_number
            }
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Department</InputLabel>
            <Select
              name="basicEmployeeData.department_id"
              value={formik.values.basicEmployeeData?.department_id || ""}
              onChange={formik.handleChange}
              label="Department"
              disabled={departmentsLoading}
            >
              <MenuItem value="">Select Department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.department_name || dept.name}
                </MenuItem>
              ))}
            </Select>
            {departmentsLoading && (
              <FormHelperText>Loading departments...</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Designation"
            name="basicEmployeeData.designation"
            value={formik.values.basicEmployeeData?.designation || ""}
            onChange={formik.handleChange}
            error={
              formik.touched.basicEmployeeData?.designation &&
              Boolean(formik.errors.basicEmployeeData?.designation)
            }
            helperText={
              formik.touched.basicEmployeeData?.designation &&
              formik.errors.basicEmployeeData?.designation
            }
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Salary"
            name="basicEmployeeData.salary"
            value={formatSalary(formik.values.basicEmployeeData?.salary || "")}
            onChange={(e) => {
              const parsed = parseSalary(e.target.value);
              formik.setFieldValue("basicEmployeeData.salary", parsed);
            }}
            error={
              formik.touched.basicEmployeeData?.salary &&
              Boolean(formik.errors.basicEmployeeData?.salary)
            }
            helperText={
              formik.touched.basicEmployeeData?.salary &&
              formik.errors.basicEmployeeData?.salary
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">TZS</InputAdornment>
              ),
            }}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date Joined"
            name="basicEmployeeData.date_joined"
            type="date"
            value={formik.values.basicEmployeeData?.date_joined || ""}
            onChange={formik.handleChange}
            error={
              formik.touched.basicEmployeeData?.date_joined &&
              Boolean(formik.errors.basicEmployeeData?.date_joined)
            }
            helperText={
              formik.touched.basicEmployeeData?.date_joined &&
              formik.errors.basicEmployeeData?.date_joined
            }
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Employment Type</InputLabel>
            <Select
              name="basicEmployeeData.employment_type"
              value={formik.values.basicEmployeeData?.employment_type || ""}
              onChange={formik.handleChange}
              error={
                formik.touched.basicEmployeeData?.employment_type &&
                Boolean(formik.errors.basicEmployeeData?.employment_type)
              }
              label="Employment Type"
            >
              <MenuItem value="full time">Full Time</MenuItem>
              <MenuItem value="part time">Part Time</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="intern">Intern</MenuItem>
              <MenuItem value="volunteer">Volunteer</MenuItem>
            </Select>
            {formik.touched.basicEmployeeData?.employment_type &&
              formik.errors.basicEmployeeData?.employment_type && (
                <FormHelperText error>
                  {formik.errors.basicEmployeeData?.employment_type}
                </FormHelperText>
              )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="NSSF Number"
            name="basicEmployeeData.nssf"
            value={formik.values.basicEmployeeData?.nssf || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="INSURANCE Number"
            name="basicEmployeeData.bima"
            value={formik.values.basicEmployeeData?.bima || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="HELSB Number"
            name="basicEmployeeData.helsb"
            value={formik.values.basicEmployeeData?.helsb || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Bank Name"
            name="basicEmployeeData.bank_name"
            value={formik.values.basicEmployeeData?.bank_name || ""}
            onChange={formik.handleChange}
            error={
              formik.touched.basicEmployeeData?.bank_name &&
              Boolean(formik.errors.basicEmployeeData?.bank_name)
            }
            helperText={
              formik.touched.basicEmployeeData?.bank_name &&
              formik.errors.basicEmployeeData?.bank_name
            }
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Account Number"
            name="basicEmployeeData.account_number"
            value={formik.values.basicEmployeeData?.account_number || ""}
            onChange={formik.handleChange}
            error={
              formik.touched.basicEmployeeData?.account_number &&
              Boolean(formik.errors.basicEmployeeData?.account_number)
            }
            helperText={
              formik.touched.basicEmployeeData?.account_number &&
              formik.errors.basicEmployeeData?.account_number
            }
            required
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Role Assignment Section */}
      <Typography variant="h6" sx={{ mb: 3, color: "primary.main" }}>
        <RequiredLabel>Role Assignment</RequiredLabel>
      </Typography>

      {rolesLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading roles...</Typography>
        </Box>
      ) : (
        <Box>
          {/* Info Alert */}
          {/* <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Select one or more roles for this employee. Each role defines the
              permissions and access levels the employee will have in the
              system.
            </Typography>
          </Alert> */}

          {/* Quick Actions */}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const allRoleIds = availableRoles.map((role) => role.id);
                formik.setFieldValue("role_ids", allRoleIds);
              }}
              disabled={
                formik.values.role_ids?.length === availableRoles.length
              }
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                formik.setFieldValue("role_ids", []);
              }}
              disabled={!formik.values.role_ids?.length}
            >
              Clear All
            </Button>
            {formik.values.role_ids?.length > 0 && (
              <Typography
                variant="body2"
                sx={{ alignSelf: "center", color: "text.secondary" }}
              >
                {formik.values.role_ids.length} of {availableRoles.length} roles
                selected
              </Typography>
            )}
          </Box>

          {/* Role Selection Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {availableRoles.map((role) => {
              const isSelected = formik.values.role_ids?.includes(role.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={role.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: isSelected ? "2px solid" : "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      backgroundColor: isSelected
                        ? "primary.50"
                        : "background.paper",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: isSelected
                          ? "primary.50"
                          : "action.hover",
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => {
                      const currentRoles = formik.values.role_ids || [];
                      let newRoles;

                      if (isSelected) {
                        // Remove role if already selected
                        newRoles = currentRoles.filter((id) => id !== role.id);
                      } else {
                        // Add role if not selected
                        newRoles = [...currentRoles, role.id];
                      }

                      formik.setFieldValue("role_ids", newRoles);
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor: isSelected
                              ? "primary.main"
                              : "grey.400",
                            backgroundColor: isSelected
                              ? "primary.main"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1,
                          }}
                        >
                          {isSelected && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "white",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: isSelected ? "bold" : "normal",
                            color: isSelected ? "primary.main" : "text.primary",
                          }}
                        >
                          {role.role_name}
                        </Typography>
                      </Box>

                      {role.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {role.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Selected Roles Summary */}
          {formik.values.role_ids && formik.values.role_ids.length > 0 && (
            <Paper
              sx={{
                p: 2,
                backgroundColor: "success.50",
                border: "1px solid",
                borderColor: "success.200",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: "success.dark", fontWeight: "bold" }}
              >
                ✓ Selected Roles ({formik.values.role_ids.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formik.values.role_ids.map((roleId) => {
                  const role = availableRoles.find((r) => r.id === roleId);
                  return role ? (
                    <Chip
                      key={role.id}
                      label={role.role_name}
                      color="success"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: "medium" }}
                    />
                  ) : null;
                })}
              </Box>
            </Paper>
          )}

          {/* Error Message */}
          {formik.touched.role_ids && formik.errors.role_ids && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formik.errors.role_ids}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

const EmployeeForm = ({ editMode = false, initialData = null, onSuccess }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedSteps, setSavedSteps] = useState(new Set());

  // State for roles
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);

  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);

  // State for regions and districts
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setDepartmentsLoading(true);
        const response = await departmentsAPI.getAllDepartments();
        console.log("Fetched Departments:", response);
        // Backend returns departments directly in data field
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
        showError("Failed to load departments");
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  // Fetch regions on component mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoadingRegions(true);
        const response = await locationAPI.getAllRegions();
        setRegions(response.data || []);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        showError("Failed to load regions");
      } finally {
        setLoadingRegions(false);
      }
    };

    fetchRegions();
  }, []);

  // Fetch districts when in edit mode and region_id is set
  useEffect(() => {
    if (editMode && initialData?.personalEmployeeData?.region_id) {
      handleRegionChange(initialData.personalEmployeeData.region_id);
    }
  }, [editMode, initialData]);

  // Handle region change to fetch districts
  const handleRegionChange = async (regionId) => {
    if (!regionId) {
      setDistricts([]);
      return;
    }

    try {
      setLoadingDistricts(true);
      const response = await locationAPI.getDistrictsByRegion(regionId);
      setDistricts(response.data || []);
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      showError("Failed to load districts");
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Fetch available roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const response = await rolesAPI.getAllRoles();
        const roles = response.data || response;
        setAvailableRoles(Array.isArray(roles) ? roles : []);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        showError("Failed to load available roles");
        setAvailableRoles([]);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch user roles if in edit mode
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (editMode && initialData?.id) {
        try {
          const roleIds = initialData.roles?.map((role) => role.id) || [];
          setUserRoles(roleIds);
        } catch (error) {
          console.error("Failed to fetch user roles:", error);
          setUserRoles([]);
        }
      }
    };

    if (editMode && initialData && availableRoles.length > 0) {
      fetchUserRoles();
    }
  }, [editMode, initialData, availableRoles]);

  const personalInfoSchema = Yup.object({
    first_name: Yup.string().required("First name is required"),
    sur_name: Yup.string().required("Surname is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone_number: Yup.string()
      .required("Phone number is required")
      .matches(
        /^(06|07)\d{8}$/,
        "Phone number must be 10 digits starting with 06 or 07"
      )
      .length(10, "Phone number must be exactly 10 digits"),
    gender: Yup.string().required("Gender is required"),
    bioData: Yup.object({
      dob: Yup.date().required("Date of birth is required"),
      national_id: Yup.string()
        .test(
          "nida-format",
          "Invalid NIDA format. Must be 20 digits",
          validateNIDA
        )
        .required("National ID is required"),
    }),
  });

  // Initialize form data
  const getInitialFormData = () => {
    if (editMode && initialData) {
      return {
        // Basic Info
        first_name: initialData.first_name || "",
        middle_name: initialData.middle_name || "",
        sur_name: initialData.sur_name || "",
        email: initialData.email || "",
        phone_number: initialData.phone_number || "",
        gender: initialData.gender || "",
        status: initialData.status || "active",

        // Bio Data
        bioData: {
          dob: initialData.bioData?.dob || "",
          marital_status: initialData.bioData?.marital_status || "",
          blood_group: initialData.bioData?.blood_group || "",
          national_id: initialData.bioData?.national_id || "",
          fingerprint_id: initialData.bioData?.fingerprint_id || "",
          signature: initialData.bioData?.signature || "",
        },

        // Personal Data
        personalEmployeeData: {
          region_id: initialData.personalEmployeeData?.region_id || "",
          district_id: initialData.personalEmployeeData?.district_id || "",
          education_level:
            initialData.personalEmployeeData?.education_level || "",
        },
        address: initialData.address || "",
        emergency_contacts: initialData.emergencyContacts || initialData.emergency_contacts || [
          { name: "", phone_number: "", relationship: "" },
        ],
        next_of_kin: initialData.nextOfKin || initialData.next_of_kin || [
          { name: "", phone_number: "", relationship: "", percentage: 100 },
        ],

        // Employment Data
        basicEmployeeData: {
          department_id: initialData.basicEmployeeData?.department_id || "",
          designation: initialData.basicEmployeeData?.designation || "",
          salary: initialData.basicEmployeeData?.salary || "",
          date_joined: initialData.basicEmployeeData?.date_joined || "",
          employment_type: initialData.basicEmployeeData?.employment_type || "",
          registration_number:
            initialData.basicEmployeeData?.registration_number || "",
          supervisor_id: initialData.basicEmployeeData?.supervisor_id || "",
          status: initialData.basicEmployeeData?.status || "active",
          nssf: initialData.basicEmployeeData?.nssf || "",
          bima: initialData.basicEmployeeData?.bima || "",
          helsb: initialData.basicEmployeeData?.helsb || "",
          bank_name: initialData.basicEmployeeData?.bank_name || "",
          account_number: initialData.basicEmployeeData?.account_number || "",
        },

        // Role Assignment
        role_ids: userRoles || [],
      };
    }

    return {
      // Basic Info
      first_name: "",
      middle_name: "",
      sur_name: "",
      email: "",
      phone_number: "",
      gender: "",
      status: "active",

      // Bio Data
      bioData: {
        dob: "",
        marital_status: "",
        blood_group: "",
        national_id: "",
        signature: "",
      },

      // Personal Data
      personalEmployeeData: {
        region_id: "",
        district_id: "",
        education_level: "",
      },
      address: "",
      emergency_contacts: [{ name: "", phone_number: "", relationship: "" }],
      next_of_kin: [
        { name: "", phone_number: "", relationship: "", percentage: 100 },
      ],

      // Employment Data
      basicEmployeeData: {
        department_id: "",
        designation: "",
        salary: "",
        date_joined: "",
        employment_type: "",
        registration_number: "",
        supervisor_id: "",
        status: "active",
        nssf: "",
        bima: "",
        helsb: "",
        bank_name: "",
        account_number: "",
      },

      // Role Assignment
      role_ids: [],
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());

  // Get validation schema for current step
  const getValidationSchema = () => {
    switch (activeStep) {
      case 0:
        return personalInfoSchema;
      case 1:
        return contactPersonalDataSchema;
      case 2:
        return employmentRolesSchema;
      default:
        return Yup.object({});
    }
  };

  const formik = useFormik({
    initialValues: formData,
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setTouched }) => {
      // Mark all fields as touched when submitting to show validation errors
      const allFields = Object.keys(values);
      const touchedFields = {};
      allFields.forEach(field => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields);
      
      if (activeStep === steps.length - 1) {
        await handleSubmit(values);
      } else {
        setFormData(values);
        setActiveStep((prev) => prev + 1);
      }
    },
  });

  // Update validation schema when step changes
  useEffect(() => {
    formik.setErrors({});
    formik.setTouched({});
  }, [activeStep]);

  // Update formData when userRoles are loaded in edit mode
  useEffect(() => {
    if (
      editMode &&
      userRoles.length > 0 &&
      formik.values.role_ids.length === 0
    ) {
      formik.setFieldValue("role_ids", userRoles);
    }
  }, [userRoles, editMode]);

  // Validate current step
  const validateCurrentStep = async () => {
    const currentSchema = getValidationSchema();
    try {
      await currentSchema.validate(formik.values, { abortEarly: false });
      return true;
    } catch (validationErrors) {
      const errors = {};
      const touched = {};

      validationErrors.inner.forEach((error) => {
        const path = error.path;
        if (path.includes(".")) {
          const pathParts = path.split(".");
          if (!errors[pathParts[0]]) {
            errors[pathParts[0]] = {};
          }
          errors[pathParts[0]][pathParts[1]] = error.message;

          if (!touched[pathParts[0]]) {
            touched[pathParts[0]] = {};
          }
          touched[pathParts[0]][pathParts[1]] = true;
        } else {
          errors[path] = error.message;
          touched[path] = true;
        }
      });

      formik.setErrors(errors);
      formik.setTouched(touched);
      return false;
    }
  };

  // Save current step data
  const handleSaveStep = async () => {
    const isStepValid = await validateCurrentStep();

    if (!isStepValid) {
      showError("Please fix the errors before saving");
      return;
    }

    if (!editMode) {
      showError("Save is only available in edit mode");
      return;
    }

    setSaveLoading(true);
    setError(null);

    try {
      const currentStepData = getCurrentStepData();
      await employeesAPI.updatePartial(initialData.id, currentStepData);

      setSavedSteps((prev) => new Set([...prev, activeStep]));
      showSuccess(`${steps[activeStep].label} saved successfully`);
    } catch (err) {
      console.error("Error saving step:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to save step data";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSaveLoading(false);
    }
  };

  // Get current step data for partial update
  const getCurrentStepData = () => {
    switch (activeStep) {
      case 0:
        return {
          first_name: formik.values.first_name,
          middle_name: formik.values.middle_name,
          sur_name: formik.values.sur_name,
          email: formik.values.email,
          phone_number: formik.values.phone_number,
          gender: formik.values.gender,
          status: formik.values.status,
          bioData: formik.values.bioData,
        };
      case 1:
        return {
          personalEmployeeData: formik.values.personalEmployeeData,
          address: formik.values.address,
          emergency_contacts: formik.values.emergency_contacts,
          next_of_kin: formik.values.next_of_kin,
        };
      case 2:
        return {
          basicEmployeeData: formik.values.basicEmployeeData,
          role_ids: formik.values.role_ids,
        };
      default:
        return {};
    }
  };

  // Handle final submission
  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    try {
      // Generate default password using firstname format: Firstname@2025 (first letter capitalized)
      const defaultPassword = `${values.first_name.charAt(0).toUpperCase() + values.first_name.slice(1).toLowerCase()}@2025`;

      const apiData = {
        first_name: values.first_name,
        middle_name: values.middle_name,
        sur_name: values.sur_name,
        email: values.email,
        phone_number: values.phone_number,
        gender: values.gender,
        status: values.status,
        address: values.address,
        ...(!editMode && { password: defaultPassword }),

        basic_employee_data: values.basicEmployeeData,
        bio_data: {
          ...values.bioData,
          dob: values.bioData.dob
            ? new Date(values.bioData.dob).toISOString().split("T")[0]
            : null,
        },
        personal_employee_data: values.personalEmployeeData,
        emergency_contacts: values.emergency_contacts,
        next_of_kin: values.next_of_kin,
        roles: values.role_ids || [],
      };

      let response;
      if (editMode && initialData?.id) {
        response = await employeesAPI.update(initialData.id, apiData);
        showSuccess("Employee updated successfully");
      } else {
        response = await employeesAPI.create(apiData);
        showSuccess("Employee created successfully");
      }

      if (onSuccess && response.data) {
        onSuccess(response.data.data || response.data);
      } else {
        navigate(ROUTES.EMPLOYEES);
      }
    } catch (err) {
      console.error("Error saving employee:", err);

      // Extract detailed error messages
      let errorMessage = "Failed to save employee data";

      if (err.response?.data) {
        const { message, errors } = err.response.data;

        if (errors && Array.isArray(errors) && errors.length > 0) {
          // Format validation errors into readable message with line breaks
          const errorList = errors
            .map((e) => {
              // Handle errors with or without field property
              if (e.field) {
                // Format field name: convert snake_case to Title Case
                const fieldName = e.field
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
                  .replace(/\./g, " → "); // Handle nested fields like bioData.dob
                return `• ${fieldName}: ${e.message}`;
              } else {
                // If no field, just show the message
                return `• ${e.message}`;
              }
            })
            .join("\n");
          errorMessage = `${errorList}`;
        } else {
          // Use general message if no specific errors
          errorMessage = message || errorMessage;
        }
      }

      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Allow direct navigation to any step
  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleNext = async () => {
    // For last step, always proceed to submit
    if (activeStep === steps.length - 1) {
      const isStepValid = await validateCurrentStep();
      if (isStepValid) {
        await handleSubmit(formik.values);
      }
      return;
    }

    // For other steps, just move to next without validation requirement
    setFormData(formik.values);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setFormData(formik.values);
    setActiveStep((prev) => prev - 1);
  };

  const handleCancel = () => {
    navigate(ROUTES.EMPLOYEES);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <PersonalInfoForm formik={formik} editMode={editMode} />; // Add editMode prop
      case 1:
        return (
          <ContactPersonalDataForm
            formik={formik}
            regions={regions}
            districts={districts}
            loadingRegions={loadingRegions}
            loadingDistricts={loadingDistricts}
            onRegionChange={handleRegionChange}
          />
        );
      case 2:
        return (
          <EmploymentRolesForm
            formik={formik}
            departments={departments}
            departmentsLoading={departmentsLoading}
            availableRoles={availableRoles}
            rolesLoading={rolesLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          {editMode ? "Edit Employee" : "Add New Employee"}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            Failed to save employee:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {error}
          </Typography>
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={
                    savedSteps.has(index) && editMode ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      step.icon
                    )
                  }
                  optional={
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                      {savedSteps.has(index) && editMode && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          ✓ Saved
                        </Typography>
                      )}
                    </Typography>
                  }
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: "pointer" }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ mb: 4 }}>{renderStepContent()}</Box>

          <Divider sx={{ mb: 3 }} />

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Button variant="outlined" onClick={handleCancel} sx={{ mr: 2 }}>
                Cancel
              </Button>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  startIcon={<NavigateBeforeIcon />}
                >
                  Back
                </Button>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Save Button - Show in edit mode for all steps */}
              {editMode && (
                <LoadingButton
                  variant="outlined"
                  onClick={handleSaveStep}
                  loading={saveLoading}
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  sx={{
                    color: savedSteps.has(activeStep)
                      ? "success.main"
                      : "primary.main",
                    borderColor: savedSteps.has(activeStep)
                      ? "success.main"
                      : "primary.main",
                  }}
                >
                  {savedSteps.has(activeStep) ? "Saved" : "Save"}
                </LoadingButton>
              )}

              {/* Next/Submit Button */}
              <LoadingButton
                variant="contained"
                onClick={handleNext}
                loading={loading && activeStep === steps.length - 1}
                disabled={loading || (activeStep === 2 && rolesLoading)}
                endIcon={
                  activeStep === steps.length - 1 ? null : <NavigateNextIcon />
                }
              >
                {activeStep === steps.length - 1
                  ? editMode
                    ? "Update Employee"
                    : "Create Employee"
                  : "Next"}
              </LoadingButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmployeeForm;
